import { normalizeTikTokCreatorInfo } from '@shared/tiktok-creator-info';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import {
  type TikTokCreatorInfoResult,
  tikTokCreatorInfoService,
} from '@/services/data/TikTokCreatorInfoService';

/**
 * The TikTok account's current posting options. TikTok's audit rules ask for a
 * fresh query each time the posting page renders, so this refetches on mount
 * (the post editor opening a TikTok post). While it loads, or if TikTok cannot
 * be reached, `info` is the copy stored on the connection (`seed`), and `state`
 * says how fresh it is. With neither, the card offers only the most private
 * audience.
 */
export function useTikTokCreatorInfo(options: {
  connectionId?: string | null;
  seed?: unknown;
  enabled: boolean;
}) {
  const { user, loading: authLoading } = useAuth();
  const seedInfo = normalizeTikTokCreatorInfo(options.seed);

  const query = useQuery<TikTokCreatorInfoResult>({
    queryKey: ['tiktok-creator-info', user?.id, options.connectionId ?? null],
    queryFn: async () => {
      const response = await tikTokCreatorInfoService.refresh(options.connectionId ?? undefined);
      if (response.error || !response.data) {
        throw new Error(response.error?.message ?? 'The TikTok check failed');
      }
      return response.data;
    },
    enabled: options.enabled && !!user?.id && !authLoading,
    staleTime: 0,
    gcTime: 60_000,
    refetchOnMount: 'always',
    retry: false,
  });

  const info = query.data?.info ?? seedInfo;
  return {
    info,
    isRefreshing: query.isPending && options.enabled,
    state: query.data?.state ?? (query.isError ? 'unavailable' : null),
    connectionId: query.data?.connectionId ?? options.connectionId ?? null,
  };
}
